import { Badge, Checkbox, Text } from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import {
  type PermissionCatalogDefinition,
  type PermissionGrant,
  type PermissionGroupAction,
} from "@/lib/types";
import { PermissionPageIcon } from "@/components/features/settings/permission-groups/PermissionPageIcon";

type PermissionActionsChange = (
  permissionId: string,
  actions: PermissionGroupAction[],
  isNotification: boolean,
) => void;

type PermissionPageNode = {
  permission: PermissionCatalogDefinition;
  children: PermissionPageNode[];
};

const hasPageGrant = (
  permission: PermissionCatalogDefinition,
  grants: PermissionGrant[],
) =>
  grants.some(
    (grant) => grant.permissionId === permission.id && grant.action === "view",
  );

const isAncestorOf = (
  ancestorId: string,
  permissionId: string,
  catalogById: Map<string, PermissionCatalogDefinition>,
  visited = new Set<string>(),
): boolean => {
  if (ancestorId === permissionId) return true;
  if (visited.has(permissionId)) return false;

  visited.add(permissionId);
  const permission = catalogById.get(permissionId);
  return Boolean(
    permission?.parentPermissionIds.some((parentId) =>
      isAncestorOf(ancestorId, parentId, catalogById, new Set(visited)),
    ),
  );
};

const getTreeParentId = (
  permission: PermissionCatalogDefinition,
  catalog: PermissionCatalogDefinition[],
  catalogById: Map<string, PermissionCatalogDefinition>,
) => {
  const availableParents = permission.parentPermissionIds.filter((parentId) =>
    catalogById.has(parentId),
  );
  if (availableParents.length <= 1) return availableParents.at(0);

  const namespace = permission.id.split(".").at(0);
  const sameNamespaceParents = availableParents.filter(
    (parentId) => parentId.split(".").at(0) === namespace,
  );
  if (sameNamespaceParents.length === 1) return sameNamespaceParents[0];
  if (!sameNamespaceParents.length) return availableParents[0];

  const commonAncestors = catalog.filter(
    (candidate) =>
      candidate.id !== permission.id &&
      candidate.id.split(".").at(0) === namespace &&
      sameNamespaceParents.every((parentId) =>
        isAncestorOf(candidate.id, parentId, catalogById),
      ),
  );

  return commonAncestors.sort(
    (left, right) => right.id.length - left.id.length,
  )[0]?.id;
};

const buildPermissionTree = (catalog: PermissionCatalogDefinition[]) => {
  const catalogById = new Map(
    catalog.map((permission) => [permission.id, permission]),
  );
  const nodesById = new Map<string, PermissionPageNode>(
    catalog.map((permission) => [permission.id, { permission, children: [] }]),
  );
  const roots: PermissionPageNode[] = [];

  catalog.forEach((permission) => {
    const node = nodesById.get(permission.id);
    const parentId = getTreeParentId(permission, catalog, catalogById);
    const parent = parentId ? nodesById.get(parentId) : undefined;

    if (node && parent) parent.children.push(node);
    else if (node) roots.push(node);
  });

  return roots;
};

const flattenNode = (node: PermissionPageNode): PermissionPageNode[] => [
  node,
  ...node.children.flatMap(flattenNode),
];

export const getSelectablePageIds = (catalog: PermissionCatalogDefinition[]) =>
  new Set(
    buildPermissionTree(
      catalog.filter((permission) => permission.kind === "page"),
    )
      .flatMap(flattenNode)
      .filter((node) => !node.children.length)
      .map(({ permission }) => permission.id),
  );

const includeRequiredPageParents = (
  grants: PermissionGrant[],
  catalog: PermissionCatalogDefinition[],
) => {
  const catalogById = new Map(
    catalog.map((permission) => [permission.id, permission]),
  );
  const nextGrants = [...grants];
  let addedParent = true;

  while (addedParent) {
    addedParent = false;
    for (const grant of nextGrants) {
      if (grant.action !== "view") continue;
      const permission = catalogById.get(grant.permissionId);
      if (permission?.kind !== "page" || !permission.parentPermissionIds.length)
        continue;
      if (
        permission.parentPermissionIds.some((parentPermissionId) =>
          nextGrants.some(
            (candidate) =>
              candidate.permissionId === parentPermissionId &&
              candidate.action === "view",
          ),
        )
      )
        continue;
      const parentPermission = permission.parentPermissionIds
        .map((parentPermissionId) => catalogById.get(parentPermissionId))
        .find((candidate) =>
          candidate?.actions.some(({ action }) => action === "view"),
        );
      if (!parentPermission) continue;
      nextGrants.push({ permissionId: parentPermission.id, action: "view" });
      addedParent = true;
    }
  }

  return nextGrants;
};

export const normalizeInferredPageGrants = (
  grants: PermissionGrant[],
  catalog: PermissionCatalogDefinition[],
) => {
  const pageCatalog = catalog.filter(
    (permission) => permission.kind === "page",
  );
  const roots = buildPermissionTree(pageCatalog);
  const branchIds = new Set(
    roots
      .flatMap(flattenNode)
      .filter((node) => node.children.length)
      .map(({ permission }) => permission.id),
  );
  const explicitGrants = grants.filter(
    (grant) => grant.action !== "view" || !branchIds.has(grant.permissionId),
  );
  const selectedPageIds = new Set(
    explicitGrants
      .filter(
        (grant) =>
          grant.action === "view" &&
          pageCatalog.some(
            (permission) => permission.id === grant.permissionId,
          ),
      )
      .map((grant) => grant.permissionId),
  );
  const inferredGrants: PermissionGrant[] = [];

  const inferBranch = (node: PermissionPageNode): boolean => {
    const hasSelectedDescendant = node.children.map(inferBranch).some(Boolean);
    if (node.children.length && hasSelectedDescendant) {
      selectedPageIds.add(node.permission.id);
      inferredGrants.push({
        permissionId: node.permission.id,
        action: "view",
      });
    }
    return selectedPageIds.has(node.permission.id) || hasSelectedDescendant;
  };

  roots.forEach(inferBranch);
  return includeRequiredPageParents(
    [...explicitGrants, ...inferredGrants],
    catalog,
  );
};

function PermissionPageTree({
  nodes,
  grants,
  onActionsChange,
}: {
  nodes: PermissionPageNode[];
  grants: PermissionGrant[];
  onActionsChange: PermissionActionsChange;
}) {
  const defaultOpenSections = nodes
    .filter((node) => node.children.length)
    .map(({ permission }) => permission.id);

  return (
    <Accordion
      type="multiple"
      defaultValue={defaultOpenSections}
      className="space-y-1"
    >
      {nodes.map((node) => (
        <PermissionPageTreeNode
          key={node.permission.id}
          node={node}
          grants={grants}
          onActionsChange={onActionsChange}
        />
      ))}
    </Accordion>
  );
}

function PermissionPageTreeNode({
  node,
  grants,
  onActionsChange,
}: {
  node: PermissionPageNode;
  grants: PermissionGrant[];
  onActionsChange: PermissionActionsChange;
}) {
  const { permission, children } = node;
  const viewAction = permission.actions.find(
    ({ action }) => action === "view",
  )?.action;
  const isEnabled = hasPageGrant(permission, grants);

  if (!children.length) {
    return (
      <div className="hover:bg-muted/50 flex items-center justify-between gap-4 rounded-md px-2 py-2 transition-colors">
        <span className="flex min-w-0 items-center gap-2">
          <PermissionPageIcon permissionId={permission.id} />
          <Text variant="body" className="truncate">
            {permission.label}
          </Text>
        </span>
        <Checkbox
          checked={isEnabled}
          disabled={!viewAction}
          aria-label={`View ${permission.label}`}
          onCheckedChange={(checked) => {
            onActionsChange(
              permission.id,
              checked === true && viewAction ? [viewAction] : [],
              false,
            );
          }}
        />
      </div>
    );
  }

  const selectableDescendants = flattenNode(node).filter(
    (descendant) => !descendant.children.length,
  );
  const enabledDescendantCount = selectableDescendants.filter(
    ({ permission: descendantPermission }) =>
      hasPageGrant(descendantPermission, grants),
  ).length;

  return (
    <AccordionItem value={permission.id} className="border-0">
      <div className="hover:bg-muted/50 flex items-center rounded-md pr-2 transition-colors">
        <AccordionTrigger
          iconPosition="left"
          className="min-w-0 justify-start gap-2 px-2 py-2 hover:no-underline"
        >
          <span className="flex min-w-0 items-center gap-2">
            <PermissionPageIcon permissionId={permission.id} />
            <span className="truncate">{permission.label}</span>
          </span>
        </AccordionTrigger>
        <Badge
          variant="secondary"
          className="shrink-0 tabular-nums"
          aria-label={`${enabledDescendantCount} selected pages in ${permission.label}`}
        >
          {enabledDescendantCount}{" "}
          {enabledDescendantCount === 1 ? "page" : "pages"}
        </Badge>
      </div>
      <AccordionContent className="border-border/60 ml-4 border-l pt-1 pb-1 pl-3">
        <PermissionPageTree
          nodes={children}
          grants={grants}
          onActionsChange={onActionsChange}
        />
      </AccordionContent>
    </AccordionItem>
  );
}

export function PermissionPageAccordion({
  catalog,
  grants,
  onActionsChange,
}: {
  catalog: PermissionCatalogDefinition[];
  grants: PermissionGrant[];
  onActionsChange: PermissionActionsChange;
}) {
  return (
    <PermissionPageTree
      nodes={buildPermissionTree(catalog)}
      grants={grants}
      onActionsChange={onActionsChange}
    />
  );
}
